import { FC } from "react";
import { Form } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PureNumberInput from "@Components/PureNumberInput/PureNumberInput";
import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";
import { ConversionInputs } from "src/components";
import { getFieldConfig } from "./constants";
import { useTopupDetails } from "../../context/TopupDetailsContext";
import { getMinMaxBounds } from "../../utils";
import { RootState } from "src/store";

type RequiredFieldType = {
  name: string;
  displayName: string;
  type: string;
};

type Props = {
  requiredFields?: RequiredFieldType[] | null;
  isFetching?: boolean;
};

const FieldSkeleton = () => (
  <div className="flex flex-col gap-2">
    <SkeletonBox className="h-3 w-24" />
    <SkeletonBox className="h-[52px] w-full rounded-md" />
  </div>
);

const RequiredFieldsLoading: FC<{ count?: number }> = ({ count = 2 }) => (
  <div className="border-border-default flex flex-col gap-8 border-b p-4">
    <SkeletonBox className="h-5 w-40" />
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <FieldSkeleton key={i} />
      ))}
    </div>
  </div>
);

const FieldError: FC<{ name: string }> = ({ name }) => {
  const form = Form.useFormInstance();
  return (
    <Form.Item shouldUpdate noStyle>
      {() => {
        const error = form.getFieldError(name)[0];
        return error ? <div className="mt-1 text-xs text-font-danger">{error}</div> : <div></div>;
      }}
    </Form.Item>
  );
};

const RequiredFields: FC<Props> = ({ requiredFields, isFetching }) => {
  const { t } = useTranslation();
  const { item } = useTopupDetails();
  const payPoints = Form.useWatch("payPoints");
  const totalPointsBalance = useSelector((state: RootState) => state.client.userDetails.totalPointsBalance) ?? 0;

  const { from: minAmount, to: maxAmount } = getMinMaxBounds(item?.serviceChargeList ?? []);

  if (isFetching) {
    return <RequiredFieldsLoading count={requiredFields?.length || 2} />;
  }
  if (!requiredFields?.length) return null;

  return (
    <div className="border-border-default flex flex-col gap-4">
      <div className="text-base font-medium">{t("Topup.enterRequiredInfo")}</div>
      <div className="flex flex-col gap-4 divide-border-default divide-y">
        <div className="border-border-default flex flex-col gap-2 py-4">
          {requiredFields.map((field) => {
            const { component, rules } = getFieldConfig(field.type);
            return (
              <div key={field.name} className="flex flex-col gap-1">
                <div className="text-sm font-medium">{field.displayName}</div>
                <Form.Item name={field.name} noStyle rules={rules}>
                  {component(field.displayName)}
                </Form.Item>
                <FieldError name={field.name} />
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-1">
          <ConversionInputs
            fromLabel={t("Topup.balance")}
            toLabel={t("Topup.amount")}
            fromInput={
              <Form.Item
                name="getAmount"
                noStyle
                rules={[
                  {
                    validator(_, value) {
                      if (value === undefined || value === null || value === "") return Promise.reject(new Error(t("Topup.fieldRequired")));
                      if (value >= minAmount && value < maxAmount) return Promise.resolve();
                      return Promise.reject(new Error(t("Topup.amountOutOfRange")));
                    },
                  },
                ]}
              >
                <PureNumberInput
                  className="p-3! w-full!"
                  placeholder={t("Topup.youWillGet")}
                  suffix={<div className="text-font-active font-medium">EGP</div>}
                />
              </Form.Item>
            }
            toInput={
              <Form.Item name="payAmount" noStyle>
                <PureNumberInput
                  disabled
                  className="p-3! w-full!"
                  placeholder={t("Topup.youWillPay")}
                  suffix={<div className="text-font-active font-medium">EGP</div>}
                />
              </Form.Item>
            }
          />
          <Form.Item
            className="m-0! hidden"
            name="payPoints"
            noStyle
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null) return Promise.resolve();
                  if (value > totalPointsBalance) return Promise.reject(new Error(t("Topup.insufficientPoints")));
                  return Promise.resolve();
                },
              },
            ]}
          ></Form.Item>

          <div className="text-font-active grid grid-cols-2 items-center text-xs font-medium">
            <FieldError name="getAmount" />
            <div className="flex items-center justify-end">
              {payPoints}
              {t("Topup.pointsSuffix")} *
            </div>
          </div>
          <FieldError name="payPoints" />
        </div>
      </div>
    </div>
  );
};

export default RequiredFields;
